const PROFILE_KEY = 'mtib_attendance_profile_v1';
const HOST_SECRET_KEY = 'mtib_attendance_host_secrets_v1';
const PROJECTOR_SECRET_KEY = 'mtib_attendance_projector_secrets_v1';
const PENDING_ADMIN_KEY = 'mtib_attendance_pending_admin_v1';
const PENDING_STAFF_AUTH_EMAIL_KEY = 'mtib_kehadiran_pending_staff_auth_email_v1';
const CREATE_MEETING_DRAFT_KEY = 'mtib_kehadiran_create_meeting_draft_v18';
const CREATE_MEETING_DRAFT_VERSION = 3;

function safeParse<T>(value: string | null, fallback: T): T {
  if (!value) return fallback;
  try { return JSON.parse(value) as T; } catch { return fallback; }
}

export interface ExternalGuestIdentity {
  email: string;
  fullName?: string;
  departmentOrg?: string;
}

export interface CreateMeetingDraft {
  title: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  fullDay: boolean;
  sameTimeEveryDay?: boolean;
  locationType: 'MTIB_ROOM' | 'EXTERNAL';
  externalLocation: string;
  chairman: string;
  expected: string[];
  cohosts?: string[];
  excludedDates?: string[];
  dailySchedule: Array<{
    date: string;
    startTime: string;
    endTime: string;
    rooms: Array<{ clientId: string; roomId: string | null; startTime: string; endTime: string; useMeetingTime?: boolean }>;
  }>;
}

function createMeetingDraft(value: unknown): CreateMeetingDraft | null {
  if (!value || typeof value !== 'object') return null;
  const draft = value as Partial<CreateMeetingDraft>;
  if (typeof draft.title !== 'string' || typeof draft.startDate !== 'string' || typeof draft.endDate !== 'string'
    || typeof draft.startTime !== 'string' || typeof draft.endTime !== 'string'
    || typeof draft.fullDay !== 'boolean'
    || (draft.locationType !== 'MTIB_ROOM' && draft.locationType !== 'EXTERNAL')
    || typeof draft.externalLocation !== 'string' || typeof draft.chairman !== 'string'
    || !Array.isArray(draft.expected) || !draft.expected.every((item) => typeof item === 'string')
    || !Array.isArray(draft.dailySchedule)) return null;
  const dailySchedule = draft.dailySchedule.slice(0, 31).map((occurrence) => {
    if (!occurrence || typeof occurrence !== 'object') return null;
    const item = occurrence as CreateMeetingDraft['dailySchedule'][number];
    if (typeof item.date !== 'string' || typeof item.startTime !== 'string' || typeof item.endTime !== 'string' || !Array.isArray(item.rooms)) return null;
    const rooms = item.rooms.slice(0, 30).map((room) => (
      room && typeof room === 'object' && typeof room.clientId === 'string'
      && (room.roomId === null || typeof room.roomId === 'string')
      && typeof room.startTime === 'string' && typeof room.endTime === 'string'
      && (room.useMeetingTime === undefined || typeof room.useMeetingTime === 'boolean') ? room : null
    ));
    if (rooms.some((room) => room === null)) return null;
    return { date: item.date, startTime: item.startTime, endTime: item.endTime, rooms: rooms as CreateMeetingDraft['dailySchedule'][number]['rooms'] };
  });
  if (dailySchedule.some((occurrence) => occurrence === null)) return null;
  return {
    title: draft.title.slice(0, 180), startDate: draft.startDate, endDate: draft.endDate,
    startTime: draft.startTime, endTime: draft.endTime, fullDay: draft.fullDay, sameTimeEveryDay: draft.sameTimeEveryDay ?? false,
    locationType: draft.locationType,
    externalLocation: draft.externalLocation.slice(0, 240), chairman: draft.chairman.slice(0, 160),
    expected: draft.expected.map((item) => item.slice(0, 200)).slice(0, 200),
    cohosts: Array.isArray(draft.cohosts) ? draft.cohosts.filter((item): item is string => typeof item === 'string').map((item) => item.toLowerCase()).slice(0, 30) : [],
    excludedDates: Array.isArray(draft.excludedDates) ? draft.excludedDates.filter((item): item is string => typeof item === 'string').slice(0, 31) : [],
    dailySchedule: dailySchedule as CreateMeetingDraft['dailySchedule'],
  };
}

function migrateV1Draft(value: unknown): CreateMeetingDraft | null {
  if (!value || typeof value !== 'object') return null;
  const draft = value as Record<string, unknown>;
  if (typeof draft.title !== 'string' || typeof draft.date !== 'string'
    || typeof draft.startTime !== 'string' || typeof draft.endTime !== 'string'
    || typeof draft.fullDay !== 'boolean'
    || (draft.locationType !== 'MTIB_ROOM' && draft.locationType !== 'EXTERNAL')
    || typeof draft.externalLocation !== 'string' || typeof draft.chairman !== 'string'
    || !Array.isArray(draft.expected) || !draft.expected.every((item) => typeof item === 'string')) return null;
  const roomId = typeof draft.selectedRoomId === 'string' ? draft.selectedRoomId : null;
  return createMeetingDraft({
    title: draft.title,
    startDate: draft.date,
    endDate: draft.date,
    startTime: draft.startTime,
    endTime: draft.endTime,
    fullDay: draft.fullDay,
    locationType: draft.locationType,
    externalLocation: draft.externalLocation,
    chairman: draft.chairman,
    expected: draft.expected,
    dailySchedule: draft.date && draft.startTime && draft.endTime ? [{
      date: draft.date,
      startTime: draft.startTime,
      endTime: draft.endTime,
      rooms: roomId ? [{ clientId: `migrated-${roomId}`, roomId, startTime: draft.startTime, endTime: draft.endTime }] : [],
    }] : [],
  });
}

function storedCreateMeetingDraft(value: unknown, ownerSubject: string): CreateMeetingDraft | null {
  if (!value || typeof value !== 'object') return null;
  const stored = value as { version?: unknown; ownerSubject?: unknown; data?: unknown };
  if (typeof stored.ownerSubject !== 'string'
    || stored.ownerSubject !== ownerSubject) return null;
  if (stored.version === CREATE_MEETING_DRAFT_VERSION || stored.version === 2) return createMeetingDraft(stored.data);
  if (stored.version === 1) return migrateV1Draft(stored.data);
  return null;
}

function clearCreateMeetingDraftStorage(): void {
  try {
    sessionStorage.removeItem(CREATE_MEETING_DRAFT_KEY);
  } catch {
    // Storage is resilience only; failure to clear must never fail an action.
  }
}

function externalGuestEmail(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const email = value.trim().toLowerCase();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) || email.endsWith('@mtib.gov.my')) return null;
  return email;
}

export const storage = {
  getProfile(): ExternalGuestIdentity | null {
    const stored = safeParse<{ email?: unknown; fullName?: unknown; departmentOrg?: unknown } | null>(localStorage.getItem(PROFILE_KEY), null);
    const email = externalGuestEmail(stored?.email);
    if (!email) {
      localStorage.removeItem(PROFILE_KEY);
      return null;
    }
    const identity = {
      email,
      ...(typeof stored?.fullName === 'string' && stored.fullName.trim() ? { fullName: stored.fullName.trim().slice(0, 120) } : {}),
      ...(typeof stored?.departmentOrg === 'string' && stored.departmentOrg.trim() ? { departmentOrg: stored.departmentOrg.trim().slice(0, 160) } : {}),
    };
    localStorage.setItem(PROFILE_KEY, JSON.stringify(identity));
    return identity;
  },
  setProfile(profile: ExternalGuestIdentity) {
    const email = externalGuestEmail(profile.email);
    if (!email) throw new Error('Only an external guest email may be stored.');
    localStorage.setItem(PROFILE_KEY, JSON.stringify({
      email,
      ...(profile.fullName?.trim() ? { fullName: profile.fullName.trim().slice(0, 120) } : {}),
      ...(profile.departmentOrg?.trim() ? { departmentOrg: profile.departmentOrg.trim().slice(0, 160) } : {}),
    }));
  },
  clearProfile() {
    localStorage.removeItem(PROFILE_KEY);
    sessionStorage.removeItem(PENDING_ADMIN_KEY);
  },
  getPendingAdminEmail(): string | null {
    return sessionStorage.getItem(PENDING_ADMIN_KEY);
  },
  setPendingAdminEmail(email: string) {
    sessionStorage.setItem(PENDING_ADMIN_KEY, email.trim().toLowerCase());
  },
  clearPendingAdminEmail() {
    sessionStorage.removeItem(PENDING_ADMIN_KEY);
  },
  getPendingStaffAuthEmail(): string | null {
    return sessionStorage.getItem(PENDING_STAFF_AUTH_EMAIL_KEY);
  },
  setPendingStaffAuthEmail(email: string) {
    sessionStorage.setItem(PENDING_STAFF_AUTH_EMAIL_KEY, email.trim().toLowerCase());
  },
  clearPendingStaffAuthEmail() {
    sessionStorage.removeItem(PENDING_STAFF_AUTH_EMAIL_KEY);
  },
  getCreateMeetingDraft(ownerSubject: string | null): CreateMeetingDraft | null {
    if (!ownerSubject) {
      clearCreateMeetingDraftStorage();
      return null;
    }
    let raw: string | null;
    try {
      raw = sessionStorage.getItem(CREATE_MEETING_DRAFT_KEY);
    } catch {
      return null;
    }
    if (!raw) return null;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw) as unknown;
    } catch {
      clearCreateMeetingDraftStorage();
      return null;
    }
    const draft = storedCreateMeetingDraft(parsed, ownerSubject);
    if (!draft) clearCreateMeetingDraftStorage();
    return draft;
  },
  setCreateMeetingDraft(ownerSubject: string | null, draft: CreateMeetingDraft) {
    if (!ownerSubject) return;
    try {
      sessionStorage.setItem(CREATE_MEETING_DRAFT_KEY, JSON.stringify({
        version: CREATE_MEETING_DRAFT_VERSION,
        ownerSubject,
        data: draft,
      }));
    } catch {
      // The form remains fully functional when session storage is unavailable.
    }
  },
  clearCreateMeetingDraft() {
    clearCreateMeetingDraftStorage();
  },
  getHostSecret(meetingId: string): string | null {
    const map = safeParse<Record<string, string>>(localStorage.getItem(HOST_SECRET_KEY), {});
    return map[meetingId] ?? null;
  },
  setHostSecret(meetingId: string, secret: string) {
    const map = safeParse<Record<string, string>>(localStorage.getItem(HOST_SECRET_KEY), {});
    map[meetingId] = secret;
    localStorage.setItem(HOST_SECRET_KEY, JSON.stringify(map));
  },
  removeHostSecret(meetingId: string) {
    const map = safeParse<Record<string, string>>(localStorage.getItem(HOST_SECRET_KEY), {});
    delete map[meetingId];
    localStorage.setItem(HOST_SECRET_KEY, JSON.stringify(map));
  },
  getProjectorSecret(meetingId: string): string | null {
    const map = safeParse<Record<string, string>>(localStorage.getItem(PROJECTOR_SECRET_KEY), {});
    return map[meetingId] ?? null;
  },
  setProjectorSecret(meetingId: string, secret: string) {
    const map = safeParse<Record<string, string>>(localStorage.getItem(PROJECTOR_SECRET_KEY), {});
    map[meetingId] = secret;
    localStorage.setItem(PROJECTOR_SECRET_KEY, JSON.stringify(map));
  },
  removeProjectorSecret(meetingId: string) {
    const map = safeParse<Record<string, string>>(localStorage.getItem(PROJECTOR_SECRET_KEY), {});
    delete map[meetingId];
    localStorage.setItem(PROJECTOR_SECRET_KEY, JSON.stringify(map));
  },
};
